import { useCallback, useState } from 'react';
import {
	type DiagramDirectTransition,
	type DiagramSharedTransition,
	toPortKey,
} from './toPortInfo.tsx';
import { isDirectOrSharedTransitionWithDefinedPorts } from './isEveryPropertyDefined.tsx';
import type {
	ReroutingTransition,
	OnTransitionReroutingMovePayload,
	OnTransitionReroutingClosePayload,
} from './ReroutingTransitionValid.types.tsx';

const initialState: ReroutingTransition = {
	stage: 'none',
};

type isInvalidConnectionPortPayload = {
	transition: DiagramDirectTransition | DiagramSharedTransition;
	proposedPortKey: string;
	proposedStatusId: string;
	originalStatusId: string;
	side: 'source' | 'target';
};

export const isInvalidConnectionPort = ({
	transition,
	proposedPortKey,
	originalStatusId,
	proposedStatusId,
	side,
}: isInvalidConnectionPortPayload): boolean => {
	if (side === 'source' && isDirectOrSharedTransitionWithDefinedPorts(transition)) {
		if (transition.type === 'shared') {
			return (
				// TODO HIX-4369 Handle multiple targetIndices
				transition.links.some(
					(links) => links.source !== originalStatusId && links.source === proposedStatusId,
				) || proposedPortKey === toPortKey(transition.target, transition.links[0].targetIndex)
			);
		}

		return proposedPortKey === toPortKey(transition.target, transition.targetIndex);
	}

	if (side === 'target' && isDirectOrSharedTransitionWithDefinedPorts(transition)) {
		if (transition.type === 'shared') {
			const portKeys = transition.links.map((link) => toPortKey(link.source, link.sourceIndex));
			return portKeys.includes(proposedPortKey);
		}

		return proposedPortKey === toPortKey(transition.source, transition.sourceIndex);
	}

	return false;
};

/** Handles the 'reconnecting transition start/end ports' feature.
 *
 * This is internally called 'rerouting' and a transition being 'rerouted'
 * can exist in 3 stages `'none'`, `'started'`, and `'valid'` as per the
 * state shape of {@link ReroutingTransition}
 */
export const useTransitionRerouting = () => {
	const [reroutingTransition, setReroutingTransition] = useState<ReroutingTransition>(initialState);

	const onTransitionReroutingCancel = useCallback(() => {
		setReroutingTransition({ stage: 'none' });
	}, []);

	const onTransitionReroutingMove = useCallback((value: OnTransitionReroutingMovePayload) => {
		setReroutingTransition((prev) => {
			if (prev.stage === 'none') {
				const startingState: ReroutingTransition = { stage: 'started', ...value };
				return startingState;
			}

			const movingState: ReroutingTransition = { ...prev, ...value };
			return movingState;
		});
	}, []);

	const onTransitionReroutingClose = useCallback(
		({ proposedPortKey, proposedStatusId }: OnTransitionReroutingClosePayload) => {
			setReroutingTransition((prev) => {
				if (prev.stage === 'none') {
					return prev;
				}

				if (
					proposedPortKey === undefined ||
					isInvalidConnectionPort({
						transition: prev.transition,
						proposedPortKey,
						proposedStatusId,
						originalStatusId: prev.originalStatusId,
						side: prev.side,
					})
				) {
					const startingState: ReroutingTransition = {
						stage: 'started',
						transition: prev.transition,
						side: prev.side,
						currentPosition: prev.currentPosition,
						originalStatusId: prev.originalStatusId,
						originalPortIndex: prev.originalPortIndex,
						originalPortKey: prev.originalPortKey,
					};

					return startingState;
				}

				const completedState: ReroutingTransition = {
					stage: 'valid',
					transition: prev.transition,
					side: prev.side,
					currentPosition: prev.currentPosition,
					originalStatusId: prev.originalStatusId,
					originalPortIndex: prev.originalPortIndex,
					originalPortKey: prev.originalPortKey,
					proposedPortKey,
				};

				return completedState;
			});
		},
		[],
	);

	return [
		reroutingTransition,
		{ onTransitionReroutingMove, onTransitionReroutingClose, onTransitionReroutingCancel },
	] as const;
};
